import { Store } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "../../ui";

/**
 * Physical-shop stock for one product.
 *
 * Upstream this costs one request per SKU, so it is not part of a sweep. It
 * arrives either from the watch tick or from opening this page: if nothing
 * has been recorded, or the reading is stale, the panel fetches once on
 * mount. That is deliberate — the watch tick alone left every unwatched
 * product with an empty table and a panel that rendered nothing, which is
 * indistinguishable from the feature being broken.
 */

type StoreRow = {
	warehouseCode: string;
	name: string;
	city: string | null;
	availability: number;
	updatedAt: number;
};

type Payload = {
	supported: boolean;
	checkedAt: number | null;
	stores: StoreRow[];
	alertCodes: string[];
};

/** Re-check on open once a reading is older than this. */
const STALE_AFTER_MS = 15 * 60 * 1000;

const LEVEL_LABEL: Record<number, string> = {
	2: "in stock",
	1: "a few left",
	0: "not in shop",
};

function relativeTime(ts: number): string {
	const mins = Math.round((Date.now() - ts) / 60_000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.round(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	return `${Math.round(hours / 24)}d ago`;
}

export function StoreInventoryPanel({ productId }: { productId: number }) {
	const [data, setData] = useState<Payload | null>(null);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showAll, setShowAll] = useState(false);

	const refresh = useCallback(async (): Promise<void> => {
		setRefreshing(true);
		setError(null);
		try {
			const res = await fetch(`/api/products/${productId}/store-inventory/refresh`, { method: "POST" });
			if (!res.ok) throw new Error();
			setData((await res.json()) as Payload);
		} catch {
			setError("Could not reach the store.");
		} finally {
			setRefreshing(false);
		}
	}, [productId]);

	useEffect(() => {
		let cancelled = false;
		setData(null);
		setError(null);
		setShowAll(false);

		fetch(`/api/products/${productId}/store-inventory`)
			.then((r) => (r.ok ? (r.json() as Promise<Payload>) : Promise.reject()))
			.then((d) => {
				if (cancelled) return;
				setData(d);
				const stale = d.checkedAt === null || Date.now() - d.checkedAt > STALE_AFTER_MS;
				if (d.supported && stale) void refresh();
			})
			.catch(() => {
				if (!cancelled) setError("Could not load shop stock.");
			});

		return () => {
			cancelled = true;
		};
	}, [productId, refresh]);

	// Stores that cannot report shops get no panel at all — there is nothing
	// here to be missing.
	if (!data?.supported) return null;

	const stocked = data.stores.filter((s) => s.availability > 0);
	const alerts = new Set(data.alertCodes);
	const shown = showAll ? data.stores : stocked;

	return (
		<section>
			<header className="mb-1.5 flex min-h-7 flex-wrap items-center gap-2">
				<h2 className="text-[13px] font-semibold text-ink">In store</h2>
				<span className="text-xs text-muted">
					{refreshing
						? "Checking shops…"
						: data.checkedAt
							? `${stocked.length} of ${data.stores.length} shops · checked ${relativeTime(data.checkedAt)}`
							: "Not checked yet"}
				</span>
				<div className="ml-auto flex items-center gap-1">
					{data.stores.length > stocked.length && (
						<button
							type="button"
							onClick={() => setShowAll((v) => !v)}
							className="min-h-tap rounded-md px-2.5 text-xs font-medium text-muted hover:bg-raised hover:text-ink sm:h-7 sm:min-h-0"
						>
							{showAll ? "Only shops with stock" : "Show all shops"}
						</button>
					)}
					<Button variant="secondary" size="sm" onClick={() => void refresh()} disabled={refreshing}>
						{refreshing ? "Checking…" : "Check now"}
					</Button>
				</div>
			</header>

			{error && (
				<p className="rounded-[10px] border border-dashed border-line px-3 py-2.5 text-xs text-muted">{error}</p>
			)}

			{!error && data.stores.length === 0 && (
				<p className="rounded-[10px] border border-dashed border-line px-3 py-2.5 text-xs text-muted">
					{refreshing
						? "Checking which shops carry this…"
						: "No shop stock recorded yet. Some stores only check the shops you pick in Settings."}
				</p>
			)}

			{!error && data.stores.length > 0 && shown.length === 0 && (
				<p className="rounded-[10px] border border-dashed border-line px-3 py-2.5 text-xs text-muted">
					Not carried by any shop right now.
				</p>
			)}

			{!error && shown.length > 0 && (
				<ul className="grid grid-cols-1 gap-1 sm:grid-cols-2 2xl:grid-cols-3">
					{shown.map((s) => (
						<li
							key={s.warehouseCode}
							className="flex items-center gap-2 rounded-lg border border-hairline bg-paper px-2.5 py-1.5"
						>
							<Store className={`size-4 shrink-0 ${s.availability > 0 ? "text-ink" : "text-muted"}`} aria-hidden />
							<span className="min-w-0 flex-1 truncate text-xs text-ink">
								{s.name}
								{s.city ? <span className="text-muted"> · {s.city}</span> : null}
							</span>
							{alerts.has(s.warehouseCode) && (
								<span className="text-[11px] text-muted" title="Restock alerts are on for this shop">
									Alerting
								</span>
							)}
							<span className={`shrink-0 text-[11px] ${s.availability > 0 ? "font-semibold text-live" : "text-muted"}`}>
								{LEVEL_LABEL[s.availability] ?? "unknown"}
							</span>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}
